/**
 * Creating or editing a dish, as a page.
 *
 * This was the largest dialog in the product — thirteen fields across three
 * tabs, a photo, serving windows and offers — and a dialog is the wrong
 * container for it on a device with an on-screen keyboard. It renders through a
 * portal, so it sits outside the screen that knows how much of the viewport the
 * keyboard covers, and every field near the bottom has to be corrected by hand.
 * A page gets that from `Screen`, once, and a merchant filling this in on a
 * phone gets the whole viewport and the browser's own back gesture.
 *
 * The form itself is `ItemFormLayout`, rendered from one `useItemForm`
 * controller. This screen's job is only what the controller is handed: the dish
 * the route names, and the restaurant's settings.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CircleHelp } from 'lucide-react-native';
import { fetchMerchantMenu, fetchMerchantRestaurant, type MerchantMenuItem } from '@yummy/db';
import { EmptyState, LoadingState, Screen, useAsync, useLocale } from '@yummy/ui-rnr';

import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';
import { useItemForm } from '../lib/useItemForm';
import { ItemFormLayout } from './item-form/ItemFormLayout';

/**
 * Loads the item named by the route and renders the form for it.
 *
 * @returns The item form page.
 */
export function ItemFormScreen() {
  const { itemId } = useParams<{ itemId?: string }>();
  const { t } = useLocale();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;
  const isNew = itemId === undefined;

  const menu = useAsync(
    async () => (restaurantId ? await fetchMerchantMenu(supabase, restaurantId) : null),
    [restaurantId],
  );
  const settings = useAsync(
    async () => (restaurantId ? await fetchMerchantRestaurant(supabase, restaurantId) : null),
    [restaurantId],
  );

  // Back to the category this was opened from, not to the whole menu. Without
  // the category the list loses the filter and a merchant has to find their
  // place again after every item.
  const [searchParams] = useSearchParams();
  const returnCategory = searchParams.get('category');
  const back = useCallback(
    () => navigate(returnCategory ? `/menu?category=${returnCategory}` : '/menu'),
    [navigate, returnCategory],
  );
  const reload = useCallback(() => {
    menu.reload();
  }, [menu]);

  const categories = useMemo(() => menu.data?.categories ?? [], [menu.data]);
  const item = isNew ? null : (menu.data?.items.find((i) => i.id === itemId) ?? null);

  // Only the first load blanks the screen. A *reload* — after saving an offer,
  // for instance — must not unmount the form: unmounting throws away every edit
  // the merchant has typed but not yet saved, which is exactly the sort of
  // quiet data loss `useAsync`'s `initial` flag exists to prevent.
  if (menu.initial || settings.initial) return <LoadingState />;

  if (!isNew && !item) {
    return (
      <Screen>
        <EmptyState
          icon={CircleHelp}
          title={t('common.notFound')}
          actionLabel={t('merchant.nav.menu')}
          onAction={back}
        />
      </Screen>
    );
  }

  if (!restaurantId) {
    return (
      <Screen>
        <EmptyState
          icon={CircleHelp}
          title={t('merchant.menu.noRestaurant')}
          actionLabel={t('merchant.nav.menu')}
          onAction={back}
        />
      </Screen>
    );
  }

  return (
    <ItemForm
      item={item}
      categories={categories}
      currency={restaurant?.currency ?? 'SGD'}
      restaurantId={restaurantId}
      timezone={settings.data?.timezone ?? restaurant?.timezone ?? 'UTC'}
      defaultPrepMinutes={settings.data?.default_prep_minutes ?? 15}
      openTime={settings.data?.opening_hours?.[0]?.open_time ?? '09:00'}
      closeTime={settings.data?.opening_hours?.[0]?.close_time ?? '21:00'}
      onSaved={reload}
      onDone={back}
    />
  );
}

/**
 * The controller and the form over it.
 *
 * The controller is created here, below the screen's guards, so the hooks it
 * holds are never called conditionally — and so the form is handed one object
 * rather than a dozen loose values.
 *
 * @param props The dish, the restaurant settings, and the saved/finished
 *   handlers.
 * @returns The item form.
 */
function ItemForm({
  item,
  categories,
  currency,
  restaurantId,
  timezone,
  defaultPrepMinutes,
  openTime,
  closeTime,
  onSaved,
  onDone,
}: {
  item: MerchantMenuItem | null;
  categories: Array<{ id: string; name: string }>;
  currency: string;
  restaurantId: string;
  timezone: string;
  defaultPrepMinutes: number;
  openTime: string;
  closeTime: string;
  onSaved: () => void;
  onDone: () => void;
}) {
  const form = useItemForm({
    item,
    categories,
    currency,
    restaurantId,
    timezone,
    defaultPrepMinutes,
    openTime,
    closeTime,
    onSaved,
    onDone,
  });

  return <ItemFormLayout form={form} />;
}
